import { jsx, jsxs } from "react/jsx-runtime";
import { ConciergeSidebarWidget } from "@brandclub/bc-widgets";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useDomainConfig, useBrandAppConfig } from "../../api/cache/appConfigQueryOptions.js";
import { track } from "../../analytics/AnalyticsService.js";
import { AnalyticsTrackingEvent } from "../../analytics/events.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { updateConversationTitle } from "../api.js";
import { ConfirmActionDialog } from "./ConfirmActionDialog.js";
/* empty css                       */
import { IconCompose, IconChats, IconChevronUp, IconHome, IconSidebar } from "./ConciergeIcons.js";
const PAGE_SIZE = 30;
const fetchConversations = async (apiBaseUrl, limit = PAGE_SIZE, cursor) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  const response = await fetch(`${apiBaseUrl}/concierge/conversations?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error("Failed to fetch conversations");
  return response.json();
};
const deleteConversation = async (apiBaseUrl, conversationId) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/concierge/conversations/${conversationId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error("Failed to delete conversation");
};
const weightMap = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
const SidebarButton = ({ icon, label, onClick, isExpanded, ariaLabel, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClick == null ? void 0 : onClick();
        },
        "aria-label": ariaLabel,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        className: `tw:flex tw:items-center tw:h-10 tw:rounded-lg tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full ${isExpanded ? "tw:px-4 tw:gap-2" : "tw:justify-center"}`,
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-6", children: icon }),
          isExpanded && /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:font-medium tw:whitespace-nowrap tw:overflow-hidden",
              style: {
                color: resolveColor(state.labelColor),
                fontSize: state.labelFontSize,
                fontWeight: weightMap[state.labelFontWeight] ?? 500,
                lineHeight: "24px"
              },
              children: label
            }
          )
        ]
      }
    ),
    !isExpanded && isHovered && /* @__PURE__ */ jsx(
      "div",
      {
        className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
        style: {
          backgroundColor: resolveColor(state.tooltipBgColor),
          color: resolveColor(state.tooltipTextColor),
          fontSize: state.tooltipFontSize,
          fontWeight: 600,
          lineHeight: "18px",
          borderRadius: state.tooltipBorderRadius
        },
        children: label
      }
    )
  ] });
};
const LogoWithTooltip = ({
  state,
  icons,
  resolveColor,
  isExpanded,
  brandLogoUrl,
  onToggle,
  onClick
}) => {
  const [logoHovered, setLogoHovered] = useState(false);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `tw:flex tw:items-center tw:shrink-0 ${isExpanded ? "tw:p-[16px] tw:pb-[26px] tw:justify-between" : "tw:pt-3 tw:pb-[28px] tw:justify-center"}`,
      onClick: (e) => e.stopPropagation(),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onMouseEnter: () => setLogoHovered(true),
              onMouseLeave: () => setLogoHovered(false),
              className: "tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0",
              "aria-label": "Home",
              onClick: (e) => {
                e.stopPropagation();
                onClick == null ? void 0 : onClick();
              },
              children: icons.logoIconUrl || brandLogoUrl ? /* @__PURE__ */ jsx(
                "img",
                {
                  src: icons.logoIconUrl || brandLogoUrl,
                  alt: "Brand",
                  className: "tw:object-contain",
                  style: {
                    height: state.logoIconSize,
                    width: state.logoIconSize
                  }
                }
              ) : /* @__PURE__ */ jsx(IconHome, { size: state.logoIconSize, style: { color: resolveColor(state.sidebarIconColor) } })
            }
          ),
          !isExpanded && logoHovered && /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
              style: {
                backgroundColor: resolveColor(state.tooltipBgColor),
                color: resolveColor(state.tooltipTextColor),
                fontSize: state.tooltipFontSize,
                fontWeight: 600,
                lineHeight: "18px",
                borderRadius: state.tooltipBorderRadius
              },
              children: "Home"
            }
          )
        ] }),
        isExpanded && /* @__PURE__ */ jsx(
          "button",
          {
            onClick: (e) => {
              e.stopPropagation();
              onToggle == null ? void 0 : onToggle();
            },
            "aria-label": state.closeSidebarLabel,
            className: "tw:flex tw:items-center tw:justify-center tw:size-8 tw:rounded-lg tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:shrink-0 tw:border-none tw:bg-transparent tw:p-0",
            children: /* @__PURE__ */ jsx(IconSidebar, { size: state.sidebarIconSize, style: { color: resolveColor(state.closeIconColor) } })
          }
        )
      ]
    }
  );
};
const HistoryItem = ({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onShareChat,
  isEditingTitle,
  editingTitleValue,
  onStartEditTitle,
  onSaveEditTitle,
  onCancelEditTitle,
  state,
  resolveColor
}) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [localEditValue, setLocalEditValue] = useState("");
  const editInputRef = useRef(null);
  const menuRef = useRef(null);
  const dotBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const isThisEditing = isEditingTitle;
  useEffect(() => {
    var _a;
    if (isThisEditing) {
      setLocalEditValue(editingTitleValue);
      (_a = editInputRef.current) == null ? void 0 : _a.focus();
      const input = editInputRef.current;
      if (input) {
        const id = setTimeout(() => {
          input.setSelectionRange(0, input.value.length);
        }, 0);
        return () => clearTimeout(id);
      }
    }
  }, [isThisEditing, editingTitleValue]);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);
  const openMenu = useCallback(() => {
    if (dotBtnRef.current) {
      const rect = dotBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.top, left: rect.right + 4 });
    }
    setMenuOpen((p) => !p);
  }, []);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:relative",
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => {
        setHovered(false);
        if (!menuOpen) setMenuOpen(false);
      },
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: () => !deleting && !isThisEditing && onSelect(conversation.conversationId),
            style: {
              width: "100%",
              textAlign: "left",
              padding: `${state.chatItemPaddingY}px ${state.chatItemPaddingX}px`,
              paddingRight: hovered || menuOpen || isThisEditing ? 32 : state.chatItemPaddingX,
              backgroundColor: isActive || isThisEditing ? resolveColor(state.chatItemActiveBgColor, "rgba(220,221,220,0.5)") : hovered || menuOpen ? resolveColor(state.chatItemHoverBgColor, "rgba(220,221,220,0.3)") : "transparent",
              border: "none",
              cursor: deleting ? "default" : "pointer",
              transition: "background-color 0.15s ease, opacity 0.2s ease",
              borderRadius: state.chatItemBorderRadius,
              display: "block",
              opacity: deleting ? 0.5 : 1
            },
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                className: "tw:flex tw:items-center tw:gap-2",
                style: {
                  fontSize: state.chatItemFontSize,
                  fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                  color: resolveColor(state.chatItemColor),
                  lineHeight: "24px"
                },
                children: [
                  deleting && /* @__PURE__ */ jsx(
                    "svg",
                    {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      className: "tw:shrink-0",
                      style: { animation: "spin 1s linear infinite" },
                      children: /* @__PURE__ */ jsx(
                        "circle",
                        {
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: resolveColor(state.chatItemColor),
                          strokeWidth: "3",
                          strokeLinecap: "round",
                          strokeDasharray: "32",
                          strokeDashoffset: "12"
                        }
                      )
                    }
                  ),
                  isThisEditing ? /* @__PURE__ */ jsx(
                    "input",
                    {
                      ref: editInputRef,
                      type: "text",
                      value: localEditValue,
                      onChange: (e) => setLocalEditValue(e.target.value),
                      onBlur: () => {
                        const trimmed = localEditValue.trim();
                        if (trimmed && trimmed !== (conversation.title || conversation.preview)) {
                          onSaveEditTitle(conversation.conversationId, trimmed);
                        } else {
                          onCancelEditTitle();
                        }
                      },
                      onKeyDown: (e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const trimmed = localEditValue.trim();
                          if (trimmed) {
                            onSaveEditTitle(conversation.conversationId, trimmed);
                          } else {
                            onCancelEditTitle();
                          }
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          onCancelEditTitle();
                        }
                      },
                      onClick: (e) => e.stopPropagation(),
                      style: {
                        width: "100%",
                        flex: 1,
                        minWidth: 0,
                        padding: 0,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontSize: state.chatItemFontSize,
                        fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                        color: resolveColor(state.chatItemColor),
                        lineHeight: "24px"
                      },
                      "aria-label": "Edit conversation name"
                    }
                  ) : /* @__PURE__ */ jsx(
                    "span",
                    {
                      style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: state.chatItemLineClamp,
                        WebkitBoxOrient: "vertical"
                      },
                      children: conversation.title || conversation.preview
                    }
                  ),
                  conversation.status === "in_progress" && /* @__PURE__ */ jsx(
                    "svg",
                    {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      className: "tw:shrink-0",
                      style: { animation: "spin 1s linear infinite" },
                      children: /* @__PURE__ */ jsx(
                        "circle",
                        {
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: resolveColor(state.chatItemColor),
                          strokeWidth: "3",
                          strokeLinecap: "round",
                          strokeDasharray: "32",
                          strokeDashoffset: "12"
                        }
                      )
                    }
                  )
                ]
              }
            )
          }
        ),
        (hovered || menuOpen) && !deleting && /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:right-1 tw:top-1/2 tw:-translate-y-1/2", children: /* @__PURE__ */ jsx(
          "button",
          {
            ref: dotBtnRef,
            onClick: (e) => {
              e.stopPropagation();
              openMenu();
            },
            className: "tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/10 tw:transition-colors",
            "aria-label": "More options",
            children: /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "3", r: "1.5", fill: resolveColor(state.chatItemColor) }),
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "1.5", fill: resolveColor(state.chatItemColor) }),
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "13", r: "1.5", fill: resolveColor(state.chatItemColor) })
            ] })
          }
        ) }),
        menuOpen && dropdownPos && ReactDOM.createPortal(
          /* @__PURE__ */ jsxs(
            "div",
            {
              ref: menuRef,
              className: "tw:fixed tw:z-[9999] tw:rounded-xl tw:shadow-[0_4px_12px_rgba(0,0,0,0.08)] tw:border tw:border-black/8 tw:py-1 tw:min-w-[140px] tw:bg-white",
              style: {
                top: dropdownPos.top,
                left: dropdownPos.left,
                backgroundColor: resolveColor(state.expandedBgColor, "#ffffff")
              },
              children: [
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    onClick: (e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onShareChat == null ? void 0 : onShareChat(conversation.conversationId);
                    },
                    className: "tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/5 tw:transition-colors tw:text-left",
                    style: {
                      fontSize: state.chatItemFontSize,
                      color: resolveColor(state.chatItemColor)
                    },
                    children: [
                      /* @__PURE__ */ jsxs(
                        "svg",
                        {
                          width: "14",
                          height: "14",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          strokeWidth: "2",
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          children: [
                            /* @__PURE__ */ jsx("circle", { cx: "18", cy: "5", r: "3" }),
                            /* @__PURE__ */ jsx("circle", { cx: "6", cy: "12", r: "3" }),
                            /* @__PURE__ */ jsx("circle", { cx: "18", cy: "19", r: "3" }),
                            /* @__PURE__ */ jsx("path", { d: "M8.59 13.51l6.83 3.98" }),
                            /* @__PURE__ */ jsx("path", { d: "M15.41 6.51l-6.82 3.98" })
                          ]
                        }
                      ),
                      "Share chat"
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    onClick: (e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      onStartEditTitle(conversation.conversationId, conversation.title || conversation.preview || "");
                    },
                    className: "tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/5 tw:transition-colors tw:text-left",
                    style: {
                      fontSize: state.chatItemFontSize,
                      color: resolveColor(state.chatItemColor)
                    },
                    children: [
                      /* @__PURE__ */ jsxs(
                        "svg",
                        {
                          width: "14",
                          height: "14",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          strokeWidth: "2",
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          children: [
                            /* @__PURE__ */ jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }),
                            /* @__PURE__ */ jsx("path", { d: "M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })
                          ]
                        }
                      ),
                      "Edit chat"
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    onClick: (e) => {
                      e.stopPropagation();
                      setMenuOpen(false);
                      setDeleteConfirmOpen(true);
                    },
                    className: "tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/5 tw:transition-colors tw:text-left",
                    style: {
                      fontSize: state.chatItemFontSize,
                      color: "#dc2626"
                    },
                    children: [
                      /* @__PURE__ */ jsxs(
                        "svg",
                        {
                          width: "14",
                          height: "14",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          strokeWidth: "2",
                          strokeLinecap: "round",
                          strokeLinejoin: "round",
                          children: [
                            /* @__PURE__ */ jsx("path", { d: "M3 6h18" }),
                            /* @__PURE__ */ jsx("path", { d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" }),
                            /* @__PURE__ */ jsx("path", { d: "M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" })
                          ]
                        }
                      ),
                      "Delete chat"
                    ]
                  }
                )
              ]
            }
          ),
          document.body
        ),
        /* @__PURE__ */ jsx(
          ConfirmActionDialog,
          {
            open: deleteConfirmOpen,
            title: "Delete conversation?",
            message: "This action cannot be undone.",
            confirmText: "Delete",
            onCancel: () => setDeleteConfirmOpen(false),
            onConfirm: async () => {
              setDeleteConfirmOpen(false);
              setDeleting(true);
              try {
                await onDelete(conversation.conversationId);
              } finally {
                setDeleting(false);
              }
            }
          }
        )
      ]
    }
  );
};
const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  onChatsClick,
  currentConversationId,
  userProfile,
  apiBaseUrl = "/api-rewards",
  onSignOutClick,
  onHomeClick,
  brandLogoUrl,
  isExpanded: isExpandedProp,
  onToggle,
  mobileOverlay = false,
  onShareChat
}) => {
  const { data: domainConfig } = useDomainConfig();
  const { data: brandAppConfig } = useBrandAppConfig(apiBaseUrl);
  const brandLinkTextFromConfig = (() => {
    var _a;
    if (domainConfig == null ? void 0 : domainConfig.customShopDomainName) {
      return domainConfig.customShopDomainName;
    }
    const link = (_a = brandAppConfig == null ? void 0 : brandAppConfig.branchIOConfig) == null ? void 0 : _a.webAppBranchLink;
    if (!link) return void 0;
    try {
      return new URL(link).hostname;
    } catch {
      return link.replace(/^https?:\/\//, "").replace(/\/+$/, "");
    }
  })();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = isExpandedProp !== void 0;
  const isExpanded = isControlled ? isExpandedProp : internalExpanded;
  const [conversations, setConversations] = useState([]);
  const [nextCursor, setNextCursor] = useState(void 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const hasInProgressConversations = conversations.some((c) => c.status === "in_progress");
  const handleToggle = () => {
    if (isControlled) onToggle == null ? void 0 : onToggle();
    else setInternalExpanded((p) => !p);
  };
  const queryClient = useQueryClient();
  const { isLoading, data: conversationsQueryData } = useQuery({
    queryKey: ["conversations", apiBaseUrl],
    queryFn: () => fetchConversations(apiBaseUrl, PAGE_SIZE),
    enabled: isExpanded,
    staleTime: 5 * 6e4,
    gcTime: 10 * 6e4
  });
  useEffect(() => {
    if (conversationsQueryData) {
      setConversations(conversationsQueryData.conversations);
      setNextCursor(conversationsQueryData.nextCursor);
    } else {
      setConversations([]);
      setNextCursor(void 0);
    }
  }, [conversationsQueryData]);
  useEffect(() => {
    if (!isExpanded || !hasInProgressConversations) {
      return;
    }
    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["conversations", apiBaseUrl] });
    }, 3e3);
    return () => window.clearInterval(intervalId);
  }, [isExpanded, hasInProgressConversations, queryClient, apiBaseUrl]);
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const result = await fetchConversations(apiBaseUrl, PAGE_SIZE, nextCursor);
      setConversations((prev) => [...prev, ...result.conversations]);
      setNextCursor(result.nextCursor);
    } catch {
    } finally {
      setLoadingMore(false);
    }
  }, [apiBaseUrl, nextCursor]);
  const handleDeleteConversation = useCallback(
    async (conversationId) => {
      try {
        await deleteConversation(apiBaseUrl, conversationId);
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        queryClient.invalidateQueries({ queryKey: ["conversations", apiBaseUrl] });
        track(AnalyticsTrackingEvent.CONCIERGE_CONVERSATION_DELETE, {
          conversationId,
          pageUrl: typeof window !== "undefined" ? window.location.href : ""
        }).catch(() => {
        });
        if (currentConversationId === conversationId) {
          onComposeClick == null ? void 0 : onComposeClick();
        }
      } catch {
      }
    },
    [apiBaseUrl, queryClient, currentConversationId, onComposeClick]
  );
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const handleStartEditTitle = useCallback((conversationId, title) => {
    setEditingConversationId(conversationId);
    setEditingTitleValue(title);
  }, []);
  const handleSaveEditTitle = useCallback(
    async (conversationId, newTitle) => {
      setEditingConversationId(null);
      setEditingTitleValue("");
      setConversations(
        (prev) => prev.map((c) => c.conversationId === conversationId ? { ...c, title: newTitle } : c)
      );
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        await updateConversationTitle({
          conversationId,
          title: newTitle,
          headersToInject: headers,
          apiBaseUrl
        });
        queryClient.invalidateQueries({ queryKey: ["conversations", apiBaseUrl] });
      } catch {
        queryClient.invalidateQueries({ queryKey: ["conversations", apiBaseUrl] });
      }
    },
    [apiBaseUrl, queryClient]
  );
  const handleCancelEditTitle = useCallback(() => {
    setEditingConversationId(null);
    setEditingTitleValue("");
  }, []);
  const getInitials = () => {
    const f = (userProfile == null ? void 0 : userProfile.firstName) || "";
    const l = (userProfile == null ? void 0 : userProfile.lastName) || "";
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    return "";
  };
  const initials = getInitials();
  const displayName = [userProfile == null ? void 0 : userProfile.firstName, userProfile == null ? void 0 : userProfile.lastName].filter(Boolean).join(" ") || "";
  const isSignedIn = (userProfile == null ? void 0 : userProfile.isSignedIn) ?? false;
  const [avatarHovered, setAvatarHovered] = useState(false);
  const [signOutMenuOpen, setSignOutMenuOpen] = useState(false);
  const signOutBtnRef = useRef(null);
  const signOutMenuRef = useRef(null);
  const [signOutMenuPos, setSignOutMenuPos] = useState(null);
  useEffect(() => {
    if (!signOutMenuOpen) return;
    const handleClickOutside = (e) => {
      if (signOutMenuRef.current && !signOutMenuRef.current.contains(e.target) && signOutBtnRef.current && !signOutBtnRef.current.contains(e.target)) {
        setSignOutMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [signOutMenuOpen]);
  return /* @__PURE__ */ jsx(
    ConciergeSidebarWidget,
    {
      widgetId: "concierge-sidebar",
      displayName: "Concierge Sidebar",
      wrapperStyle: {
        width: "auto",
        flexShrink: 0,
        display: "flex",
        height: "100%"
      },
      children: (widgetState, icons, resolveColor) => {
        const state = brandLinkTextFromConfig ? { ...widgetState, brandLinkText: brandLinkTextFromConfig } : widgetState;
        return /* @__PURE__ */ jsxs(
          "aside",
          {
            role: "complementary",
            "aria-expanded": isExpanded,
            onClick: !isExpanded ? handleToggle : void 0,
            className: mobileOverlay ? "tw:flex tw:flex-col tw:shrink-0 tw:h-full" : "tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full",
            style: {
              backgroundColor: resolveColor(isExpanded ? state.expandedBgColor : state.collapsedBgColor),
              width: isExpanded ? state.expandedWidth : state.collapsedWidth,
              transition: "width 0.25s ease-in-out",
              cursor: isExpanded ? void 0 : "pointer",
              borderRight: `1px solid ${resolveColor(state.borderColor)}`,
              boxSizing: "content-box",
              ...state.fontFamilyCss && state.fontFamilyCss !== "inherit" ? { fontFamily: state.fontFamilyCss } : {}
            },
            children: [
              /* @__PURE__ */ jsx(
                LogoWithTooltip,
                {
                  state,
                  icons,
                  resolveColor,
                  isExpanded,
                  brandLogoUrl,
                  onToggle: handleToggle,
                  onClick: onComposeClick
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col", children: [
                /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconCompose, { size: state.composeIconSize, style: { color: resolveColor(state.composeIconColor) } }),
                    label: state.newChatLabel,
                    onClick: onComposeClick,
                    isExpanded,
                    ariaLabel: state.newChatLabel,
                    state,
                    resolveColor
                  }
                ),
                /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconChats, { size: state.chatsIconSize, style: { color: resolveColor(state.chatsIconColor) } }),
                    label: state.chatsLabel,
                    onClick: onChatsClick,
                    isExpanded,
                    ariaLabel: state.chatsLabel,
                    state,
                    resolveColor
                  }
                )
              ] }),
              isExpanded ? /* @__PURE__ */ jsxs("div", { className: "sidebar-history-enter tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden tw:mt-[20px]", children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "tw:px-4 tw:pb-1 tw:shrink-0",
                    style: {
                      fontSize: state.chatHistoryTitleFontSize,
                      fontWeight: weightMap[state.chatHistoryTitleFontWeight] ?? 500,
                      color: resolveColor(state.chatHistoryTitleColor),
                      lineHeight: "24px"
                    },
                    children: state.chatHistoryTitle
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:overflow-y-auto tw:mt-1 tw:px-1 concierge-conversation-scroll", children: [
                  isLoading && /* @__PURE__ */ jsx("div", { className: "tw:flex tw:justify-center tw:py-4", children: /* @__PURE__ */ jsx(LoadingSpinner, { color: resolveColor(state.chatHistoryTitleColor) }) }),
                  !isLoading && conversations.length === 0 && /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "tw:text-center tw:py-4",
                      style: {
                        fontSize: state.chatItemFontSize,
                        color: resolveColor(state.chatItemColor, "#999")
                      },
                      children: "No conversations yet"
                    }
                  ),
                  !isLoading && conversations.length > 0 && /* @__PURE__ */ jsxs("div", { className: "sidebar-history-list", children: [
                    conversations.map((c) => /* @__PURE__ */ jsx(
                      HistoryItem,
                      {
                        conversation: c,
                        isActive: c.conversationId === currentConversationId,
                        onSelect: (id) => onConversationSelect == null ? void 0 : onConversationSelect(id),
                        onDelete: handleDeleteConversation,
                        onShareChat,
                        isEditingTitle: editingConversationId === c.conversationId,
                        editingTitleValue,
                        onStartEditTitle: handleStartEditTitle,
                        onSaveEditTitle: handleSaveEditTitle,
                        onCancelEditTitle: handleCancelEditTitle,
                        state,
                        resolveColor
                      },
                      c.conversationId
                    )),
                    !!nextCursor && /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: (e) => {
                          e.stopPropagation();
                          loadMore();
                        },
                        disabled: loadingMore,
                        className: "tw:w-full tw:py-2 tw:px-2.5 tw:border-none tw:bg-transparent tw:cursor-pointer hover:tw:bg-black/5 tw:transition-colors tw:rounded disabled:tw:opacity-50",
                        style: {
                          fontSize: state.chatItemFontSize,
                          fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                          color: resolveColor(state.chatItemColor),
                          opacity: 0.7
                        },
                        children: loadingMore ? "Loading..." : "Load more..."
                      }
                    )
                  ] })
                ] })
              ] }) : /* @__PURE__ */ jsx("div", { className: "tw:flex-1" }),
              isSignedIn && /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "tw:relative tw:mt-auto tw:shrink-0",
                  onClick: (e) => e.stopPropagation(),
                  style: {
                    borderTop: isExpanded ? `1px solid ${resolveColor(state.borderColor)}` : void 0,
                    backgroundColor: isExpanded ? resolveColor(state.expandedBgColor) : void 0
                  },
                  children: [
                    /* @__PURE__ */ jsxs(
                      "button",
                      {
                        ref: signOutBtnRef,
                        "aria-label": displayName || state.signOutLabel,
                        onClick: (e) => {
                          e.stopPropagation();
                          if (signOutBtnRef.current) {
                            const rect = signOutBtnRef.current.getBoundingClientRect();
                            setSignOutMenuPos({ top: rect.top - 4, left: rect.left });
                          }
                          setSignOutMenuOpen((p) => !p);
                        },
                        onMouseEnter: () => setAvatarHovered(true),
                        onMouseLeave: () => setAvatarHovered(false),
                        className: `tw:flex tw:items-center tw:cursor-pointer tw:w-full tw:rounded-none tw:transition-colors hover:tw:bg-black/5 tw:border-none tw:bg-transparent ${isExpanded ? "tw:h-16 tw:px-4 tw:gap-3" : "tw:py-3 tw:justify-center"}`,
                        children: [
                          /* @__PURE__ */ jsx(
                            "div",
                            {
                              className: "tw:flex tw:items-center tw:justify-center tw:rounded-full tw:shrink-0",
                              style: {
                                width: state.avatarSize,
                                height: state.avatarSize,
                                backgroundColor: resolveColor(state.avatarBgColor),
                                color: resolveColor(state.avatarTextColor),
                                fontSize: state.avatarFontSize,
                                fontWeight: weightMap[state.avatarFontWeight] ?? 600,
                                lineHeight: "1.5"
                              },
                              children: initials
                            }
                          ),
                          isExpanded && /* @__PURE__ */ jsx(
                            "span",
                            {
                              className: "tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis tw:flex-1 tw:text-left",
                              style: {
                                color: resolveColor(state.labelColor),
                                fontSize: 14,
                                fontWeight: weightMap[state.labelFontWeight] ?? 500,
                                lineHeight: "24px"
                              },
                              children: displayName || state.signOutLabel
                            }
                          ),
                          isExpanded && /* @__PURE__ */ jsx(
                            IconChevronUp,
                            {
                              size: 24,
                              style: {
                                color: resolveColor(state.labelColor),
                                transform: signOutMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s ease"
                              }
                            }
                          )
                        ]
                      }
                    ),
                    !isExpanded && avatarHovered && !signOutMenuOpen && /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
                        style: {
                          backgroundColor: resolveColor(state.tooltipBgColor),
                          color: resolveColor(state.tooltipTextColor),
                          fontSize: state.tooltipFontSize,
                          fontWeight: 600,
                          lineHeight: "18px",
                          borderRadius: state.tooltipBorderRadius
                        },
                        children: displayName || state.signOutLabel
                      }
                    ),
                    signOutMenuOpen && signOutMenuPos && ReactDOM.createPortal(
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          ref: signOutMenuRef,
                          className: "tw:fixed tw:z-[9999] tw:rounded-lg tw:shadow-lg tw:border tw:border-black/10 tw:py-1 tw:min-w-[140px]",
                          style: {
                            top: signOutMenuPos.top,
                            left: signOutMenuPos.left,
                            transform: "translateY(-100%)",
                            backgroundColor: resolveColor(state.expandedBgColor, "#ffffff")
                          },
                          children: /* @__PURE__ */ jsxs(
                            "button",
                            {
                              onClick: (e) => {
                                e.stopPropagation();
                                setSignOutMenuOpen(false);
                                onSignOutClick == null ? void 0 : onSignOutClick();
                              },
                              className: "tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/5 tw:transition-colors tw:text-left",
                              style: {
                                fontSize: state.labelFontSize,
                                color: resolveColor(state.labelColor)
                              },
                              children: [
                                /* @__PURE__ */ jsxs(
                                  "svg",
                                  {
                                    width: "16",
                                    height: "16",
                                    viewBox: "0 0 24 24",
                                    fill: "none",
                                    stroke: "currentColor",
                                    strokeWidth: "2",
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    children: [
                                      /* @__PURE__ */ jsx("path", { d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" }),
                                      /* @__PURE__ */ jsx("polyline", { points: "16 17 21 12 16 7" }),
                                      /* @__PURE__ */ jsx("line", { x1: "21", y1: "12", x2: "9", y2: "12" })
                                    ]
                                  }
                                ),
                                state.signOutLabel
                              ]
                            }
                          )
                        }
                      ),
                      document.body
                    )
                  ]
                }
              )
            ]
          }
        );
      }
    }
  );
};
const LoadingSpinner = ({ color }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", style: { animation: "spin 1s linear infinite" }, children: [
  /* @__PURE__ */ jsx("style", { children: `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }` }),
  /* @__PURE__ */ jsx(
    "circle",
    {
      cx: "12",
      cy: "12",
      r: "10",
      stroke: color,
      strokeWidth: "3",
      strokeLinecap: "round",
      strokeDasharray: "32",
      strokeDashoffset: "12"
    }
  )
] });
export {
  ConciergeSidebar
};
//# sourceMappingURL=ConciergeSidebar.js.map
